<!-- eslint-disable vue/no-v-html -->
<template>
  <UIDrawer
    id="team-member-schedule-drawer"
    v-model:show="internalShow"
    :width="510"
    placement="right"
    :mask-closable="true"
  >
    <UIDrawerContent
      id="team-member-schedule-content"
      :closable="true"
      :loading="isSaving"
      :disabled="(!isCustomEditMode && !isScheduleDirty) || isSaving"
      :negative-text="isMultiUser ? undefined : $t('settings.calendar.cancel')"
      :positive-text="isMultiUser ? undefined : $t('settings.calendar.save')"
      @negative-click="closeDrawer"
      @positive-click="saveChanges"
    >
      <template #header>
        <div v-if="!isMultiUser" class="flex items-center gap-3">
          <div
            class="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
          >
            <UIAvatar
              v-if="selectedUser.profilePhoto"
              :src="selectedUser.profilePhoto"
              :alt="selectedUser.name"
              class="h-10 w-10 text-base"
            />
            <span v-else class="text-base font-normal text-gray-700">
              {{ getInitials(selectedUser.name) }}
            </span>
          </div>

          <span class="text-base font-semibold text-gray-900">
            {{ selectedUser.name }}
          </span>
        </div>
        <div v-else class="px-2 py-2">
          <span class="text-base text-gray-700" v-html="multiUserDescription" />
        </div>
      </template>

      <div class="pb-2">
        <div class="mb-2">
          <UIFormItem
            :label="$t('settings.availability.schedule.schedule')"
            :show-feedback="false"
          >
            <UISelect
              id="schedule-select"
              v-model:value="selectedScheduleId"
              class="w-full"
              :options="scheduleOptions"
              :placeholder="schedulePlaceholder"
              :loading="isLoading"
              :disabled="isMultiUser"
              :filterable="true"
              :render-option="renderScheduleOption"
              @update:value="handleScheduleSelect"
            />
          </UIFormItem>
        </div>
        <div class="pb-2">
          <UITabs v-model:value="tab" type="line">
            <UITabPane
              name="weekly"
              :tab="$t('settings.availability.schedule.weekly_hours')"
              :disabled="isLoading"
              class="overflow-y-auto overflow-x-hidden [-ms-overflow-style:'none'] [scrollbar-width:'none'] [&::-webkit-scrollbar]:hidden"
            >
              <div
                v-if="isLoading"
                class="flex h-[400px] items-center justify-center"
              >
                <UISpin />
              </div>
              <div v-else>
                <WeeklyScheduleView
                  v-if="!isEditMode"
                  :schedule="currentSchedule"
                  :capitalize-abbr="capitalizeAbbr"
                />
                <!-- Multi-User Bulk Edit Mode - Show individual user schedules + Bulk Edit button -->
                <MultiUserWeeklyHoursView
                  v-else-if="isMultiUser"
                  :multi-user-schedule="multiUserSchedule as MultiUserSchedule"
                  :capitalize-abbr="capitalizeAbbr"
                  @open-bulk-edit-modal="showBulkEditWeeklyModal = true"
                />
                <!-- Single User Edit Mode -->
                <div v-else class="mt-2 flex flex-col gap-2">
                  <!-- Custom Info Banner - Only show in single-user mode -->
                  <div
                    v-if="!currentSchedule.isCustom"
                    class="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3"
                  >
                    <InfoCircleIcon class="w-5 h-5 text-blue-600" />
                    <div class="flex flex-1 items-center justify-between gap-2">
                      <span class="text-sm font-medium text-blue-600">
                        {{
                          $t(
                            'settings.availability.schedule.edit_schedule_alert'
                          )
                        }}
                      </span>
                      <UIDropdown
                        id="team-member-schedules-calendar-dropdown"
                        :options="calendars"
                        :value="selectedCalendarId"
                        placement="bottom"
                        :render-option="renderCalendarOption"
                        @clickoutside="showCalendarDropdown = false"
                        @update:show="showCalendarDropdown = true"
                      >
                        <div
                          id="dropdown-btn"
                          ref="calendarDropdownRef"
                          class="flex h-8 w-[165px] cursor-pointer items-center justify-between rounded-md border border-gray-300 bg-white pl-3 pr-1.5 py-1.5 text-sm font-normal text-gray-900 shadow-sm transition-all hover:border-indigo-200 hover:shadow-md hover:outline-none hover:ring-2 hover:ring-indigo-200"
                          :class="{
                            'border-indigo-200 ring-2 ring-indigo-200':
                              showCalendarDropdown,
                          }"
                        >
                          <span class="truncate">
                            {{ selectedCalendarsLabel }}
                          </span>
                          <ChevronDownIcon
                            class="ml-2 h-4 w-4 flex-shrink-0 text-gray-400 transition-transform"
                            :class="{
                              'rotate-180': showCalendarDropdown,
                            }"
                          />
                        </div>
                      </UIDropdown>
                    </div>
                  </div>
                  <UIFormItem
                    class="w-full"
                    :label="$t('settings.availability.schedule.timezone')"
                    :icon="Globe05Icon"
                    :show-feedback="false"
                  >
                    <UISelect
                      id="select-timezone-dropdown-schedule"
                      v-model:value="currentSchedule.timezone"
                      class="px-2"
                      :options="computedTimezones"
                      :filterable="true"
                      :placeholder="
                        $t(
                          'settings.availability.schedule.timezone_placeholder'
                        )
                      "
                      @update:value="updateTimezone"
                    />
                  </UIFormItem>
                  <WeeklyScheduleEditor
                    :schedule="currentSchedule"
                    :capitalize-abbr="capitalizeAbbr"
                    :handle-is-hour-disabled="handleIsHourDisabled"
                    :handle-is-min-disabled="handleIsMinDisabled"
                    @toggle-day-enabled="toggleDayEnabled"
                    @remove-time-slot="removeTimeSlot"
                    @time-changed="handleWeeklyTimeChanged"
                  />
                </div>
              </div>
            </UITabPane>
            <UITabPane
              class="overflow-y-auto overflow-x-hidden [-ms-overflow-style:'none'] [scrollbar-width:'none'] [&::-webkit-scrollbar]:hidden"
              name="date"
              :tab="
                $t(
                  'settings.availability.schedule.date_specific_hours_tab_name'
                )
              "
              :disabled="isLoading"
            >
              <DateSpecificScheduleView
                v-if="!isEditMode"
                :schedule="currentSchedule"
                :is-recurring-calendar-selected="
                  AppState.editCalendarValidationData.enableRecurring
                "
                @edit="handleEditSchedule('default')"
              />
              <div v-else class="mt-2 flex flex-col gap-2">
                <!-- Custom Info Banner - Never show in bulk edit (multi-user) mode -->
                <div
                  v-if="!currentSchedule.isCustom && !isMultiUser"
                  class="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3"
                >
                  <InfoCircleIcon class="w-5 h-5 text-blue-600" />
                  <div class="flex flex-1 items-center justify-between gap-2">
                    <span class="text-sm font-medium text-blue-600">
                      {{
                        $t('settings.availability.schedule.edit_schedule_alert')
                      }}
                    </span>
                    <UIDropdown
                      id="team-member-schedules-calendar-dropdown"
                      :options="calendars"
                      :value="selectedCalendarId"
                      placement="bottom"
                      :render-option="renderCalendarOption"
                      @clickoutside="showCalendarDropdown = false"
                      @update:show="showCalendarDropdown = true"
                    >
                      <div
                        id="dropdown-btn"
                        ref="calendarDropdownRef"
                        class="flex h-8 w-[165px] cursor-pointer items-center justify-between rounded-md border border-gray-300 bg-white pl-3 pr-1.5 py-1.5 text-sm font-normal text-gray-900 shadow-sm transition-all hover:border-indigo-200 hover:shadow-md hover:outline-none hover:ring-2 hover:ring-indigo-200"
                        :class="{
                          'border-indigo-200 ring-2 ring-indigo-200':
                            showCalendarDropdown,
                        }"
                      >
                        <span class="truncate">
                          {{ selectedCalendarsLabel }}
                        </span>
                        <ChevronDownIcon
                          class="ml-2 h-4 w-4 flex-shrink-0 text-gray-400 transition-transform"
                          :class="{
                            'rotate-180': showCalendarDropdown,
                          }"
                        />
                      </div>
                    </UIDropdown>
                  </div>
                </div>
                <!-- Timezone selector - only show in single-user mode (bulk edit handles timezone in modal) -->
                <UIFormItem
                  v-if="!isMultiUser"
                  class="w-full"
                  :label="$t('settings.availability.schedule.timezone')"
                  :icon="Globe05Icon"
                >
                  <UISelect
                    id="select-timezone-dropdown-schedule"
                    v-model:value="currentSchedule.timezone"
                    class="px-2"
                    :options="computedTimezones"
                    :filterable="true"
                    :placeholder="
                      $t('settings.availability.schedule.timezone_placeholder')
                    "
                    @update:value="updateTimezone"
                  />
                </UIFormItem>
                <!-- Multi-user date specific hours UI for bulk edit -->
                <MultiUserDateSpecificUI
                  v-if="isMultiUser"
                  :multi-user-schedule="multiUserSchedule as MultiUserSchedule"
                  :is-recurring-calendar-selected="isRecurringEnabledInAnyCalendar"
                  :is-saving="isSaving"
                  @update="handleMultiUserScheduleUpdate"
                  @save="handleDateSpecificSave"
                />
                <!-- Single user date specific hours UI -->
                <DateSpecificScheduleUI
                  v-else
                  :schedule="currentSchedule"
                  :toggle-recurring="() => {}"
                  :is-recurring-calendar-selected="
                    isRecurringEnabledInAnyCalendar
                  "
                />
              </div>
            </UITabPane>
            <!-- Hide Edit button in bulk edit mode (isMultiUser) -->
            <template v-if="!isMultiUser" #suffix>
              <div
                v-if="currentSchedule?.isCustom || isEditMode"
                id="edit-schedule-button"
                class="flex items-center gap-2 px-1 font-semibold text-blue-700"
                role="button"
                tabindex="0"
                :class="{
                  'opacity-100': isEditMode,
                  'cursor-pointer': !isEditMode,
                  'cursor-not-allowed opacity-50': isEditMode,
                }"
                @click="handleEditSchedule('default')"
                @keydown.enter="handleEditSchedule('default')"
              >
                <Edit01Icon class="h-4 w-4" />
                <span class="text-normal">{{
                  $t('settings.calendar.edit')
                }}</span>
              </div>
              <UIDropdown
                v-else
                id="edit-weekly-schedule-dropdown"
                :options="actionList"
                :value="selectedAction"
                :render-option="renderOption"
                placement="bottom-end"
                @clickoutside="handleClickOutside"
                @update:show="showDropdown = true"
              >
                <div
                  id="edit-schedule-button"
                  class="flex items-center gap-2 px-1 font-semibold text-blue-700"
                  role="button"
                  :class="{
                    'opacity-100': isEditMode,
                    'cursor-pointer': !isEditMode,
                    'cursor-not-allowed opacity-50': isEditMode,
                  }"
                >
                  <Edit01Icon class="h-4 w-4" />
                  <span class="text-normal">{{
                    $t('settings.calendar.edit')
                  }}</span>
                </div>
              </UIDropdown>
            </template>
          </UITabs>
        </div>
      </div>
    </UIDrawerContent>
  </UIDrawer>
  
  <!-- Bulk Edit Weekly Hours Modal -->
  <BulkEditWeeklyHoursModal
    v-model:show="showBulkEditWeeklyModal"
    :users="bulkEditUsers"
    :initial-timezone="multiUserSchedule.timezone"
    :initial-weekly-schedule="multiUserSchedule.getWeeklySchedule()"
    :is-saving="isSaving"
    @save="handleBulkEditWeeklySave"
    @cancel="showBulkEditWeeklyModal = false"
  />
</template>

<script setup lang="ts">
import Calendar from '@/models/calendar'
import AppState from '@/states/app'
import { getInitials } from '@/utils/generalFunctions'
import { computedTimezones } from '@/utils/timezonesOptions'
import {
  ChevronDownIcon,
  Edit01Icon,
  Globe05Icon,
  InfoCircleIcon,
  LinkBroken01Icon
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIAvatar,
  UIDrawer,
  UIDrawerContent,
  UIDropdown,
  UIFormItem,
  UISelect,
  UISpin,
  UITabPane,
  UITabs,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import type { PropType } from 'vue'
import { computed, h, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import calendarScheduleState from '../../states/calendarScheduleState'
import { useNotify } from '../composition/notification'
import BulkEditWeeklyHoursModal from '../Schedule/BulkEditWeeklyHoursModal.vue'
import DateSpecificScheduleUI from '../Schedule/DateSpecificScheduleUI.vue'
import { DayAvailability } from '../Schedule/DayAvailability'
import MultiUserDateSpecificUI from '../Schedule/MultiUserDateSpecificUI.vue'
import { MultiUserSchedule } from '../Schedule/MultiUserSchedule'
import MultiUserWeeklyHoursView from '../Schedule/MultiUserWeeklyHoursView.vue'
import { defaultRules, Schedule } from '../Schedule/Schedule'
import DateSpecificScheduleView from './DateSpecificScheduleView.vue'
import WeeklyScheduleEditor from './WeeklyScheduleEditor.vue'
import WeeklyScheduleView from './WeeklyScheduleView.vue'

const { t } = useI18n()
const showDropdown = ref(false)
const showCalendarDropdown = ref(false)
const selectedAction = ref(null)
const actionList = computed(() => {
  return [
    {
      label: t('settings.availability.schedule.edit_schedule'),
      key: 'edit-default-schedule',
      isDisabled: true,
    },
    {
      label: t('settings.availability.schedule.edit_default_schedule'),
      icon: Edit01Icon,
      key: 'edit-default-schedule',
      isDisabled: isScheduleEmpty.value,
      onClick: () => {
        showDropdown.value = false
        handleEditSchedule('default')
        handleClickOutside()
      },
    },
    {
      label: t('settings.availability.schedule.customize_event_type_schedule'),
      icon: LinkBroken01Icon,
      key: 'edit-custom-type-schedule',
      isDisabled: currentSchedule.value.isCustom,
      onClick: () => handleEditSchedule('custom'),
    },
  ]
})

const renderScheduleOption = ({ node, option }) => {
  let element = node
  if (option.disabled && option.tooltip) {
    element = h(
      UITooltip,
      {
        placement: 'top',
        style: { width: '100%' },
      },
      {
        trigger: () => h('div', { style: { width: '100%' } }, [node]),
        default: () => h('div', { class: 'max-w-sm' }, option.tooltip),
      }
    )
  }
  if (option.isCustom) {
    return h(
      'div',
      { class: 'pt-[6px] mt-1 border-t border-gray-200 [&>*]:m-0' },
      [element]
    )
  }
  return element
}

const renderOption = prop => {
  return h(
    'div',
    {
      class: [
        'flex flex-row items-start gap-2 px-3 py-2 cursor-pointer rounded-md transition-colors rounded-lg',
        prop.option.isDisabled ? 'opacity-50 cursor-not-allowed' : '',
        'hover:bg-gray-100',
      ],
      onClick: () => {
        if (prop.option.isDisabled) {
          return
        }
        prop.option.onClick()
      },
    },
    [
      prop.option.icon
        ? h(prop.option.icon, { class: 'w-4 h-4 text-gray-400 mt-0.5' })
        : null,
      h('div', { class: 'flex flex-col flex-1 min-w-0' }, [
        h(
          'span',
          {
            class: [
              'font-medium text-gray-700 truncate text-sm',
              prop.option.selected ? 'text-primary-700' : '',
            ],
          },
          prop.option.label
        ),
      ]),
    ]
  )
}
const renderCalendarOption = ({ option }) => {
  return h(
    'div',
    {
      class:
        'w-full max-w-full px-2 py-1 word-break whitespace-normal truncate text-gray-600 truncate-2-lines hover:bg-gray-100',
    },
    option.label
  )
}

const handleClickOutside = () => {
  showDropdown.value = false
}

// --- refs ---
const tab = ref<'weekly' | 'date'>('weekly')
const userSchedules = ref<Schedule[]>([])
const selectedScheduleId = ref<string | null>(null)
const currentSchedule = ref<Schedule>(new Schedule({}))
const isScheduleEmpty = ref(false)
const isLoading = ref(false)
const isEditMode = ref(false)
const isCustomEditMode = ref(false)
const internalShow = ref(false)
const isSaving = ref(false)
const editType = ref<'default' | 'custom'>('default')
const initialScheduleId = ref<string | null>(null)
const selectedCalendarId = ref<string | null>(null)
// Multi-user bulk edit schedule
const multiUserSchedule = ref(new MultiUserSchedule())
// US5: Track what was modified separately
const weeklyHoursModified = ref(false)
const dateSpecificHoursModified = ref(false)
// Store initial weekly schedule snapshot for comparison
const initialWeeklyScheduleSnapshot = ref<string>('')
// Bulk edit weekly hours modal
const showBulkEditWeeklyModal = ref(false)

const emit = defineEmits(['save-calendar'])

// --- variables/constants ---
const props = defineProps({
  users: { type: Array as PropType<any[]>, required: true },
  show: { type: Boolean, required: true },
  onClose: {
    type: Function as PropType<(event: MouseEvent) => void>,
    required: true,
  },
  calendar: { type: Object as PropType<Calendar | null>, required: true },
  onChangeSchedule: {
    type: Function as PropType<
      (schedule: any, teamMemberIds: string[]) => void
    >,
    required: true,
  },
})
const { sendNotification } = useNotify()

// --- computed ---
const selectedCalendarsLabel = computed(() => {
  const count = currentSchedule.value?.calendarIds?.length
  if (count <= 1) {
    return t('settings.availability.schedule.calendars_selected', {
      count,
    })
  }
  return t('settings.availability.schedule.calendars_selected_multiple', {
    count,
  })
})

const isRecurringEnabledInAnyCalendar = computed(() => {
  //if recurring is enabled for current calendar, return true
  if (AppState.editCalendarValidationData.enableRecurring) {
    return true
  }

  const calendars = AppState.globalCalendars?.calendars ?? []
  const excludedId = AppState.editingCalendar?.id
  const scheduleCalendarIds = currentSchedule.value?.calendarIds ?? []

  return calendars.some(
    calendar =>
      calendar.enableRecurring &&
      calendar.id !== excludedId &&
      scheduleCalendarIds.includes(calendar.id)
  )
})

// get list of calendars where this schedules get applied
const calendars = computed(() => {
  return (AppState.globalCalendars?.calendars || [])
    .filter(calendar =>
      currentSchedule.value?.calendarIds.includes(calendar.id)
    )
    .map(calendar => {
      return {
        label: calendar.name,
        value: calendar.id,
        key: calendar.id,
      }
    })
})

const selectedUser = computed(() => {
  return props.users[0] || { name: '', profilePhoto: '' }
})
const isMultiUser = computed(() => props.users.length > 1)
const bulkEditUsers = computed(() => {
  return props.users.map(u => ({
    id: u.id,
    name: u.name,
    profilePhoto: u.profilePhoto,
  }))
})
const multiUserDescription = computed(() => {
  const count = props.users.length
  const calName =
    props.calendar?.name ||
    t('settings.availability.schedule.selected_calendar')
  return t('settings.availability.schedule.multi_user_description', {
    count: `${count}`,
    calendar: `${calName}`,
  })
})
const scheduleOptions = computed(() => {
  //put custom schedule at the end of the list
  const customSchedule = userSchedules.value.find(s => s.isCustom)
  const otherSchedules = userSchedules.value.filter(s => !s.isCustom)
  const isRecurringEnabled = AppState.editCalendarValidationData.enableRecurring
  const options = [...otherSchedules, customSchedule]
    .filter(s => s !== undefined)
    .map(s => {
      const hasDateSpecificHours = s.getDateSpecificHours().length > 0
      const isDisabled = isRecurringEnabled && hasDateSpecificHours
      return {
        label: s.name || t('settings.availability.schedule.untitled'),
        value: s.id,
        disabled: isDisabled,
        tooltip: isDisabled
          ? t(
              'settings.availability.schedule.incompatible_schedule_with_recurring_calendar'
            )
          : undefined,
        isCustom: s.isCustom,
      }
    })
  return options
})
const schedulePlaceholder = computed(() => {
  if (isMultiUser.value) {
    return t('settings.availability.schedule.custom_schedule')
  }
  return userSchedules.value.length
    ? t('settings.availability.schedule.select_schedule')
    : t('settings.availability.schedule.no_schedules_found')
})

const isScheduleDirty = computed(() => {
  // If there was no initial schedule, but now one is selected, that's dirty
  if (!initialScheduleId.value && selectedScheduleId.value) {
    return true
  }
  // If the selected schedule changed
  if (initialScheduleId.value !== selectedScheduleId.value) {
    return true
  }
  // Fallback to your original logic
  const original = userSchedules.value.find(
    s => s.id === currentSchedule.value?.id
  )
  if (!original) {
    return false
  }
  return (
    !currentSchedule.value.isCreated || !original.equals(currentSchedule.value)
  )
})

// --- methods ---
const handleScheduleSelect = (id: string) => {
  selectedScheduleId.value = id
  currentSchedule.value = new Schedule(
    userSchedules.value.find(x => x.id === id) || {}
  )
}
const fetchSchedulesForUser = async (userId: string) => {
  isLoading.value = true

  // If calendar id is null (draft calendar), only fetch default schedules
  // The fetchSchedulesForUsers will handle this by only fetching user schedules without calendar-specific ones
  await calendarScheduleState.fetchSchedulesForUsers(
    [userId],
    props.calendar?.id,
    true
  )

  // Guard: If user switched to multi-user mode during async fetch, abort to prevent stale updates
  if (props.users.length > 1) {
    isLoading.value = false
    return
  }

  const schedules = calendarScheduleState.state.schedules[userId]
  if (schedules && schedules.length > 0) {
    //if user has custom schedule, set it as selected schedule for this calendar
    userSchedules.value = schedules.map(
      x =>
        new Schedule({
          ...x,
          name: x.isCustom
            ? t('settings.availability.schedule.custom_schedule')
            : x.name || t('settings.availability.schedule.untitled'),
        })
    )

    // For draft calendars (no id), we just need default schedules
    // Skip the calendar-specific schedule check
    if (!props.calendar?.id) {
      // For draft calendars, use the first available schedule or create an empty one
      const defaultSchedule =
        userSchedules.value.find(x => !x.isCustom) || userSchedules.value[0]
      if (defaultSchedule) {
        selectedScheduleId.value = defaultSchedule.id
        initialScheduleId.value = defaultSchedule.id
        currentSchedule.value = new Schedule(defaultSchedule)
        isScheduleEmpty.value = false
      } else {
        selectedScheduleId.value = null
        initialScheduleId.value = null
        currentSchedule.value = new Schedule({}).setAllDayUnavailable()
        isScheduleEmpty.value = true
      }
      isLoading.value = false
      return
    }

    // Check if any schedule belongs to this user and calendar
    const hasScheduleForUserAndCalendar = userSchedules.value.some(
      schedule =>
        schedule.userId === userId &&
        schedule.calendarIds.includes(props.calendar?.id || '')
    )

    if (!hasScheduleForUserAndCalendar) {
      // No schedule found for this user and calendar combination
      selectedScheduleId.value = null
      initialScheduleId.value = null
      currentSchedule.value = new Schedule({}).setAllDayUnavailable()
      isScheduleEmpty.value = true
      isLoading.value = false
      return
    }

    const scheduleInfo = calendarScheduleState.state.userSchedulesInfo[userId]
    if (scheduleInfo) {
      selectedScheduleId.value = scheduleInfo.scheduleId
      initialScheduleId.value = scheduleInfo.scheduleId
    }

    const schedule = schedules.find(x => x.id === selectedScheduleId.value)
    currentSchedule.value = schedule
      ? new Schedule(schedule)
      : new Schedule({}).setAllDayUnavailable()

    isScheduleEmpty.value = false
  } else {
    // No Schedules Found, show empty state
    userSchedules.value = []
    //set all day unavailable
    currentSchedule.value = new Schedule({})
    currentSchedule.value.rules.forEach(rule => {
      rule.intervals = []
    })
    selectedScheduleId.value = null
    isScheduleEmpty.value = true
  }
  isLoading.value = false
}
const init = async () => {
  await fetchSchedulesForUser(props.users[0].id)
}

/**
 * Initialize multi-user schedule for bulk edit mode
 * Fetches existing schedules for all selected users and preserves their date-specific hours
 */
const initializeMultiUserSchedule = async () => {
  isLoading.value = true
  
  // Reset modification tracking (US5)
  weeklyHoursModified.value = false
  dateSpecificHoursModified.value = false
  
  // Create a new MultiUserSchedule instance
  multiUserSchedule.value = new MultiUserSchedule()
  
  // Fetch schedules for all users
  const userIds = props.users.map(u => u.id)
  await calendarScheduleState.fetchSchedulesForUsers(
    userIds,
    props.calendar?.id,
    true
  )
  
  // Collect schedules for each user
  const schedules: Schedule[] = []
  const bulkEditUsers = props.users.map(user => ({
    id: user.id,
    name: user.name,
    profilePhoto: user.profilePhoto,
  }))
  
  userIds.forEach(userId => {
    const userScheduleList = calendarScheduleState.state.schedules[userId]
    if (userScheduleList && userScheduleList.length > 0) {
      // Find the schedule associated with this calendar for this user
      const calendarSchedule = userScheduleList.find(
        s => s.calendarIds?.includes(props.calendar?.id || '')
      )
      if (calendarSchedule) {
        schedules.push(new Schedule(calendarSchedule))
      } else {
        // Use default schedule if no calendar-specific schedule
        const defaultSchedule = userScheduleList.find(s => s.isDefault)
        if (defaultSchedule) {
          schedules.push(new Schedule({
            ...defaultSchedule,
            userId,
          }))
        }
      }
    }
  })
  
  // Initialize the multi-user schedule with users and their existing schedules
  multiUserSchedule.value.initializeFromUsers(
    bulkEditUsers,
    schedules,
    props.calendar?.id || '',
    AppState.locationTimezone
  )
  
  // Set the common weekly schedule from the first schedule or defaults
  if (schedules.length > 0) {
    multiUserSchedule.value.setWeeklySchedule(schedules[0].getWeeklySchedule())
    multiUserSchedule.value.setTimezone(schedules[0].timezone || AppState.locationTimezone)
  }
  
  // Sync currentSchedule for weekly hours editing (common schedule)
  currentSchedule.value = new Schedule({
    id: `bulk-schedule-${Date.now()}`,
    name: t('settings.availability.schedule.custom_schedule'),
    rules: multiUserSchedule.value.weeklySchedule,
    timezone: multiUserSchedule.value.timezone,
    calendarIds: props.calendar?.id ? [props.calendar.id] : [],
    isCreated: false,
    locationId: AppState.locationId,
    isCustom: true,
  })
  
  // Store initial weekly schedule snapshot for comparison (US5)
  initialWeeklyScheduleSnapshot.value = JSON.stringify(
    currentSchedule.value.getWeeklySchedule().map(r => ({
      day: r.day,
      isActive: r.isActive,
      intervals: r.intervals.map(i => ({ from: i.from, to: i.to }))
    }))
  )
  
  isLoading.value = false
}

/**
 * Handle updates from MultiUserDateSpecificUI (for reactivity)
 */
const handleMultiUserScheduleUpdate = () => {
  // Mark date-specific hours as modified (US5)
  dateSpecificHoursModified.value = true
  // Trigger reactivity update
  multiUserSchedule.value = multiUserSchedule.value
}

/**
 * Handle immediate save for date-specific hours from MultiUserDateSpecificUI
 * This is triggered when add/edit/delete is confirmed in the modal
 * 
 * Logic:
 * - If user has an existing custom schedule for this calendar, UPDATE it
 * - Otherwise, CREATE a new custom schedule
 * - Preserves weekly hours from existing schedules
 * - Uses the timezone passed from the modal
 */
const handleDateSpecificSave = async (timezone?: string) => {
  isSaving.value = true
  // Use passed timezone or fallback to location timezone
  const scheduleTimezone = timezone || AppState.locationTimezone
  
  try {
    // Get updated date-specific hours from multiUserSchedule for each user
    const allUserDateSpecificHours = multiUserSchedule.value.getAllUserDateSpecificHours()
    
    // Group by userId for easier processing
    const userDateHoursMap = new Map<string, DayAvailability[]>()
    allUserDateSpecificHours.forEach(entry => {
      if (!userDateHoursMap.has(entry.userId)) {
        userDateHoursMap.set(entry.userId, [])
      }
      userDateHoursMap.get(entry.userId)!.push(new DayAvailability({
        date: entry.date,
        type: 'date',
        intervals: entry.intervals,
        isActive: entry.intervals.length > 0,
      }))
    })
    
    // Process each user
    const promises = props.users.map(async user => {
      const userScheduleList = calendarScheduleState.state.schedules[user.id]
      
      // Find existing custom schedule for this calendar (can be updated)
      const existingCustomSchedule = userScheduleList?.find(
        s => s.calendarIds?.includes(props.calendar?.id || '') && s.isCustom
      )
      
      // Find any schedule for this calendar (for preserving weekly hours)
      const existingSchedule = userScheduleList?.find(
        s => s.calendarIds?.includes(props.calendar?.id || '')
      ) || userScheduleList?.find(s => s.isDefault)
      
      // Get existing weekly hours to preserve
      const existingWeeklyRules = existingSchedule
        ? existingSchedule.rules.filter(r => r.type === 'wday')
        : []
      
      // Get new date-specific hours for this user (or empty if all deleted)
      const newDateSpecificRules = userDateHoursMap.get(user.id) || []
      
      // Merge weekly rules with new date-specific rules
      const mergedRules = [...existingWeeklyRules, ...newDateSpecificRules]
      
      if (existingCustomSchedule) {
        // UPDATE existing custom schedule
        const scheduleToUpdate = new Schedule(existingCustomSchedule)
        scheduleToUpdate.rules = mergedRules
        scheduleToUpdate.timezone = scheduleTimezone
        
        // Ensure calendarIds includes current calendar
        if (props.calendar?.id && !scheduleToUpdate.calendarIds.includes(props.calendar.id)) {
          scheduleToUpdate.calendarIds.push(props.calendar.id)
        }
        
        return calendarScheduleState.updateSchedule(scheduleToUpdate)
      } else {
        // CREATE new custom schedule
        const payload = {
          rules: mergedRules,
          name: t('settings.availability.schedule.custom_schedule'),
          timezone: scheduleTimezone,
          calendarIds: props.calendar?.id ? [props.calendar.id] : [],
          isCustom: true,
          userId: user.id,
        }
        return calendarScheduleState.createSchedule(payload)
      }
    })
    
    const results = await Promise.all(promises)
    results.forEach(result => {
      props.onChangeSchedule(result, [result.userId])
    })
    
    // Re-initialize to refresh the view with updated data
    await initializeMultiUserSchedule()
    
    sendNotification({
      type: 'success',
      title: t('settings.availability.schedule.schedule_updated'),
      icon: 'CheckCircleIcon',
    })
  } catch (error) {
    console.error('Failed to save date-specific hours:', error)
    sendNotification({
      type: 'error',
      title: 'Failed to update schedules',
    })
  } finally {
    isSaving.value = false
  }
}

const updateTimezone = (value: string) => {
  currentSchedule.value?.setTimezone(value)
  // Mark weekly hours as modified (US5)
  if (isMultiUser.value) {
    weeklyHoursModified.value = true
  }
}
function capitalizeAbbr(day: string) {
  return day.charAt(0).toUpperCase() + day.slice(1, 3)
}
const handleIsHourDisabled = (
  hour: number,
  startTime?: string,
  endTime?: string
) => {
  return currentSchedule.value?.isHourDisabled(hour, startTime, endTime)
}
const handleIsMinDisabled = (
  min: number,
  hour: number | null,
  startTime?: string,
  endTime?: string
) => {
  return currentSchedule.value?.isMinuteDisabled(min, hour, startTime, endTime)
}
const toggleDayEnabled = (scheduleDay: DayAvailability) => {
  currentSchedule.value?.toggleDayEnabled(scheduleDay)
  // Mark weekly hours as modified (US5)
  if (isMultiUser.value) {
    weeklyHoursModified.value = true
  }
}
const removeTimeSlot = (scheduleDay: DayAvailability, index: number) => {
  if (scheduleDay.intervals.length === 1) {
    toggleDayEnabled(scheduleDay)
    return
  }
  scheduleDay.intervals.splice(index, 1)
  // Mark weekly hours as modified (US5)
  if (isMultiUser.value) {
    weeklyHoursModified.value = true
  }
}

// Handle time changes from WeeklyScheduleEditor (US5)
const handleWeeklyTimeChanged = () => {
  if (isMultiUser.value) {
    weeklyHoursModified.value = true
  }
}

/**
 * Handle bulk edit weekly hours save from modal
 * This saves weekly schedule for all selected users
 * 
 * Logic:
 * - Applies the selected days/times from modal to ALL selected users
 * - If user has an existing custom schedule for this calendar, UPDATE it
 * - Otherwise, CREATE a new custom schedule
 * - Preserves date-specific hours from existing schedules
 */
const handleBulkEditWeeklySave = async (data: { timezone: string; weeklySchedule: Array<{ day: string; isActive: boolean; intervals: Array<{ from: string; to: string }> }> }) => {
  isSaving.value = true
  try {
    // Convert weekly schedule data to DayAvailability format
    // These are the new weekly rules that will be applied to ALL users
    const newWeeklyRules: DayAvailability[] = data.weeklySchedule.map(day => new DayAvailability({
      day: day.day,
      isActive: day.isActive,
      intervals: day.isActive ? day.intervals : [],
      type: 'wday',
    }))
    
    // Create or update schedule for each selected user
    const promises = props.users.map(async user => {
      const userScheduleList = calendarScheduleState.state.schedules[user.id]
      
      // Find existing custom schedule for this calendar (can be updated)
      const existingCustomSchedule = userScheduleList?.find(
        s => s.calendarIds?.includes(props.calendar?.id || '') && s.isCustom
      )
      
      // Find any schedule for this calendar (for preserving date-specific hours)
      const existingSchedule = userScheduleList?.find(
        s => s.calendarIds?.includes(props.calendar?.id || '')
      ) || userScheduleList?.find(s => s.isDefault)
      
      // Get existing date-specific hours to preserve
      const existingDateSpecificRules = existingSchedule
        ? existingSchedule.rules.filter(r => r.type === 'date')
        : []
      
      // Merge new weekly rules with existing date-specific rules
      const mergedRules = [...newWeeklyRules, ...existingDateSpecificRules]
      
      if (existingCustomSchedule) {
        // UPDATE existing custom schedule
        const scheduleToUpdate = new Schedule(existingCustomSchedule)
        scheduleToUpdate.rules = mergedRules
        scheduleToUpdate.timezone = data.timezone || AppState.locationTimezone
        
        // Ensure calendarIds includes current calendar
        if (props.calendar?.id && !scheduleToUpdate.calendarIds.includes(props.calendar.id)) {
          scheduleToUpdate.calendarIds.push(props.calendar.id)
        }
        
        return calendarScheduleState.updateSchedule(scheduleToUpdate)
      } else {
        // CREATE new custom schedule
        const payload = {
          rules: mergedRules,
          name: t('settings.availability.schedule.custom_schedule'),
          timezone: data.timezone || AppState.locationTimezone,
          calendarIds: props.calendar?.id ? [props.calendar.id] : [],
          isCustom: true,
          userId: user.id,
        }
        return calendarScheduleState.createSchedule(payload)
      }
    })
    
    const results = await Promise.all(promises)
    results.forEach(result => {
      props.onChangeSchedule(result, [result.userId])
    })
    
    // Update multiUserSchedule with new weekly hours
    multiUserSchedule.value.setWeeklySchedule(newWeeklyRules)
    multiUserSchedule.value.setTimezone(data.timezone)
    
    // Re-initialize the multi-user schedule with updated data
    await initializeMultiUserSchedule()
    
    showBulkEditWeeklyModal.value = false
    
    sendNotification({
      type: 'success',
      title: t('settings.availability.schedule.schedule_updated'),
      icon: 'CheckCircleIcon',
    })
  } catch (error) {
    console.error('Failed to save bulk weekly hours:', error)
    sendNotification({
      type: 'error',
      title: 'Failed to update schedules',
    })
  } finally {
    isSaving.value = false
  }
}

const saveChanges = async () => {
  isSaving.value = true
  isEditMode.value = false
  isCustomEditMode.value = false
  try {
    if (isMultiUser.value) {
      // Sync the weekly schedule from currentSchedule to multiUserSchedule
      multiUserSchedule.value.setWeeklySchedule(currentSchedule.value.getWeeklySchedule())
      multiUserSchedule.value.setTimezone(currentSchedule.value.timezone || AppState.locationTimezone)
      
      // US5: Build existing schedules map for selective save
      const existingSchedulesMap = new Map<string, Schedule>()
      props.users.forEach(user => {
        const userScheduleList = calendarScheduleState.state.schedules[user.id]
        if (userScheduleList) {
          const calendarSchedule = userScheduleList.find(
            s => s.calendarIds?.includes(props.calendar?.id || '')
          ) || userScheduleList.find(s => s.isDefault)
          if (calendarSchedule) {
            existingSchedulesMap.set(user.id, new Schedule(calendarSchedule))
          }
        }
      })
      
      // US5: Determine what was modified
      const shouldUpdateWeekly = weeklyHoursModified.value
      const shouldUpdateDateSpecific = dateSpecificHoursModified.value
      
      // If nothing was explicitly modified, treat as both modified (legacy behavior)
      const updateBoth = !shouldUpdateWeekly && !shouldUpdateDateSpecific
      
      // Convert to individual schedules using selective save (US5)
      const individualSchedules = updateBoth
        ? multiUserSchedule.value.toIndividualSchedules()
        : multiUserSchedule.value.toIndividualSchedulesSelective(
            shouldUpdateWeekly || updateBoth,
            shouldUpdateDateSpecific || updateBoth,
            existingSchedulesMap
          )
      
      // Create custom schedule for each user with their preserved date-specific hours
      const promises = individualSchedules.map(schedule => {
        const payload = {
          rules: schedule.rules,
          name: t('settings.availability.schedule.custom_schedule'),
          timezone: schedule.timezone || AppState.locationTimezone,
          calendarIds: props.calendar?.id ? [props.calendar.id] : [],
          isCustom: true,
          userId: schedule.userId,
        }
        return calendarScheduleState.createSchedule(payload)
      })
      const results = await Promise.all(promises)
      results.forEach(result => {
        props.onChangeSchedule(result, [result.userId])
      })
      isSaving.value = false
      initialScheduleId.value = selectedScheduleId.value
      
      // Reset modification flags
      weeklyHoursModified.value = false
      dateSpecificHoursModified.value = false
      
      sendNotification({
        type: 'success',
        title: `${props.users.length} schedules updated successfully!`,
        icon: 'CheckCircleIcon',
      })
      closeDrawer()
      emit('save-calendar')
      return
    }
    // save current schedule to database (single user logic)
    if (!currentSchedule.value?.isCreated) {
      const payload = {
        rules: currentSchedule.value?.rules,
        name: currentSchedule.value?.name,
        timezone: currentSchedule.value?.timezone,
        calendarIds: props.calendar?.id ? [props.calendar.id] : [],
        isCustom: currentSchedule.value?.isCustom,
        userId: props.users[0].id,
      }
      const newSchedule = await calendarScheduleState.createSchedule(payload)
      userSchedules.value = [
        ...userSchedules.value.filter(s => s.isCreated),
        newSchedule,
      ]
      currentSchedule.value = newSchedule
      selectedScheduleId.value = newSchedule.id
      initialScheduleId.value = newSchedule.id
      isScheduleEmpty.value = false
      isSaving.value = false
      props.onChangeSchedule(currentSchedule.value, [props.users[0].id])
      sendNotification({
        type: 'success',
        title: t('settings.availability.schedule.schedule_created'),
        icon: 'CheckCircleIcon',
      })
      emit('save-calendar')
      return
    }
    if (props.calendar?.id) {
      currentSchedule.value.addCalendarId(props.calendar.id)
    }
    const updatedSchedule = await calendarScheduleState.updateSchedule(
      currentSchedule.value
    )
    currentSchedule.value = updatedSchedule
    props.onChangeSchedule(currentSchedule.value, [props.users[0].id])
    isSaving.value = false
    isEditMode.value = false

    const index = userSchedules.value.findIndex(
      x => x.id === currentSchedule.value.id
    )
    if (index !== -1) {
      userSchedules.value[index] = new Schedule(currentSchedule.value)
    } else {
      userSchedules.value = [
        ...userSchedules.value,
        new Schedule(currentSchedule.value),
      ]
    }
    userSchedules.value = [...userSchedules.value]

    initialScheduleId.value = selectedScheduleId.value
    emit('save-calendar')
    sendNotification({
      type: 'success',
      title: t('settings.availability.schedule.schedule_updated'),
      icon: 'CheckCircleIcon',
    })
  } catch (error) {
    sendNotification({
      type: 'error',
      title: t('settings.availability.schedule.error_updating_schedules'),
      icon: 'AlertCircleIcon',
    })
  } finally {
    isSaving.value = false
  }
}
const handleEditSchedule = (type: 'default' | 'custom') => {
  if (isEditMode.value) {
    return
  }
  editType.value = type
  isEditMode.value = true
  if (type === 'custom') {
    isCustomEditMode.value = true
    //create a custom schedule
    //find existing custom schedule for this user and calendar
    let newSchedule: Schedule | null = null
    const existingSchedule = userSchedules.value.find(
      x =>
        x.isCustom &&
        x.calendarIds.includes(props.calendar?.id || '') &&
        x.userId === props.users[0].id
    )
    if (props.users.length === 1 && existingSchedule) {
      if (existingSchedule?.isCreated) {
        //apply rules and timezone from existing schedule
        existingSchedule.rules = currentSchedule.value.rules
        existingSchedule.timezone = currentSchedule.value.timezone
        newSchedule = new Schedule(existingSchedule)
      } else {
        //update previous custom schedule with new rules and timezone
        newSchedule = new Schedule({
          ...existingSchedule,
          rules: currentSchedule.value.rules,
          timezone: currentSchedule.value.timezone,
        })
      }
    } else {
      newSchedule = new Schedule({
        id: `schedule-${Date.now()}`,
        name: t('settings.availability.schedule.custom_schedule'),
        rules: currentSchedule.value.rules,
        timezone: currentSchedule.value.timezone || AppState.locationTimezone,
        calendarIds: props.calendar?.id ? [props.calendar.id] : [],
        isCreated: false,
        locationId: AppState.locationId,
        isCustom: true,
        userId: props.users[0].id,
      })
    }
    if (props.users.length > 1) {
      newSchedule.rules = [...defaultRules]
      newSchedule.timezone = AppState.locationTimezone
      userSchedules.value = [newSchedule]
    } else if (!newSchedule.isCreated) {
      userSchedules.value = [...userSchedules.value, newSchedule]
    }
    selectedScheduleId.value = newSchedule.id
    currentSchedule.value = newSchedule
    isScheduleEmpty.value = false
  } else {
    isCustomEditMode.value = false
  }
}
const closeDrawer = () => {
  selectedScheduleId.value = initialScheduleId.value
  if (!isMultiUser.value) {
    props.onChangeSchedule(
      userSchedules.value.find(x => x.id === selectedScheduleId.value),
      [props.users[0]?.id || '']
    )
  }
  internalShow.value = false
  isSaving.value = false
  isEditMode.value = false
  tab.value = 'weekly'
  isCustomEditMode.value = false
  // Reset multi-user schedule on close
  multiUserSchedule.value = new MultiUserSchedule()
  // Reset modification tracking flags (US5)
  weeklyHoursModified.value = false
  dateSpecificHoursModified.value = false
  initialWeeklyScheduleSnapshot.value = ''
  props.onClose(new MouseEvent('click'))
}

// --- watchers & lifecycle ---
watch(
  () => props.show,
  val => {
    internalShow.value = val
  }
)
watch(
  () => internalShow.value,
  (val: boolean) => {
    if (!val) {
      closeDrawer()
    }
  }
)
watch(
  () => props.users,
  async val => {
    if (val.length === 1) {
      await init()
    }
    if (val.length > 1) {
      selectedScheduleId.value = null
      initialScheduleId.value = null
      userSchedules.value = []
      currentSchedule.value = new Schedule({})
      isEditMode.value = false
      
      // Initialize multi-user schedule for bulk edit
      await initializeMultiUserSchedule()
      
      handleEditSchedule('custom')
      isEditMode.value = true
      isCustomEditMode.value = true
    }
  },
  { deep: true }
)
watch(
  () => isMultiUser.value,
  multi => {
    if (multi) {
      isEditMode.value = true
    }
  },
  { immediate: true }
)
watch(
  () => selectedScheduleId.value,
  async val => {
    if (val) {
      const schedule = userSchedules.value.find(x => x.id === val) || null
      if (schedule) {
        currentSchedule.value = new Schedule(schedule)
        if (props.calendar?.id) {
          currentSchedule.value.addCalendarId(props.calendar.id)
        }
        if (
          currentSchedule.value.isCustom &&
          currentSchedule.value?.id?.includes('schedule')
        ) {
          isEditMode.value = true
        } else {
          isCustomEditMode.value
            ? (isEditMode.value = true)
            : (isEditMode.value = false)
        }
      }
    }
  }
)
</script>

<style scoped>
@keyframes slide-in {
  from {
    right: -400px;
  }
  to {
    right: 0;
  }
}
.animate-slide-in {
  animation: slide-in 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
#edit-weekly-schedule-dropdown {
  @apply rounded-md;
}
</style>
<style>
#team-member-schedules-calendar-dropdown {
  max-height: 300px !important;
  width: 180px !important;
  min-width: 180px !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  padding-left: 4px !important;
  padding-right: 4px !important;
  margin-top: 2px !important;
  overscroll-behavior: contain;
  border-radius: 8px !important;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
#team-member-schedules-calendar-dropdown::-webkit-scrollbar {
  display: none;
}
.truncate-2-lines {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  align-items: start;
}
</style>
